import React from 'react';
import {
  GroupLayoutType,
  PdfLayoutSection,
  StyleConfig,
} from '../../../utils/api/pdfLayout';
import { defaultSection } from './DocumentPreview';
import ReactTooltip from 'react-tooltip';

interface Props {
  section: PdfLayoutSection;
  layoutType: GroupLayoutType;
  columns?: string[];
  onChange: (fields: Partial<PdfLayoutSection>) => void;
}

const DEFAULT_STYLE = defaultSection(0).style_config;

const APPEARANCE_OPTIONS = [
  { value: 'border', label: 'Border' },
  { value: 'underline', label: 'Line' },
  { value: 'none', label: 'None' },
];
const NAME_FIELDS = [
  'fontStyle',
  'fontSize',
  'fontWeight',
  'color',
  'backgroundColor',
];
const DESCRIPTION_FIELDS = ['fontStyle', 'fontWeight', 'fontSize'];
const COLOR_DEFAULTS: Record<string, string> = {
  color: '#000000',
  backgroundColor: '#ffffff',
};

const FormLayout: React.FC<{
  section: PdfLayoutSection;
  onChange: (fields: Partial<PdfLayoutSection>) => void;
}> = ({ section, onChange }) => {
  return (
    <div className='ple-section-style'>
      <span className='ple-section-style__label'>
        Questions
        <i
          className='fa fa-info-circle ple-tooltip__icon'
          data-for='question-appearance-tooltip'
          data-tip='The answers will appear the same size as the question.'
        />
        <ReactTooltip
          id='question-appearance-tooltip'
          place='top'
          effect='solid'
          className='ple-tooltip'
        />
      </span>
      <div className='ple-section-style__row'>
        <div className='ple-field'>
          <span className='ple-field__label'>Appearance</span>
          <div className='ple-seg ple-seg--block'>
            {APPEARANCE_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type='button'
                className={
                  section.question_appearance === value ? 'is-active' : ''
                }
                onClick={() =>
                  onChange({
                    question_appearance:
                      value as PdfLayoutSection['question_appearance'],
                  })
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <StyleFields
          label='Label'
          fields={NAME_FIELDS}
          value={section.style_config?.question_style ?? {}}
          defaultSize={DEFAULT_STYLE.question_style?.fontSize}
          onChange={(question_style) =>
            onChange({
              style_config: {
                ...(section.style_config ?? {}),
                question_style: question_style as StyleConfig['question_style'],
              },
            })
          }
          defaultBold={true}
        />
        <div className='ple-field'>
          <span className='ple-field__label'>
            Paddding
            <i
              className='fa fa-info-circle ple-tooltip__icon'
              data-for='question-appearance-tooltip'
              data-tip='The vertical space between two questions.'
            />
            <ReactTooltip
              id='question-appearance-tooltip'
              place='top'
              effect='solid'
              className='ple-tooltip'
            />
          </span>
          <input
            className='ple-input ple-input--size'
            type='number'
            max={100}
            step={1}
            value={parseFloat(
              String(section.style_config?.question_style?.padding),
            )}
            onChange={(e) =>
              onChange({
                style_config: {
                  ...(section.style_config ?? {}),
                  question_style: {
                    ...(section.style_config?.question_style ?? {}),
                    padding:
                      e.target.value !== ''
                        ? `${Math.min(100, Math.max(1, Math.round(Number(e.target.value)) || 1))}px 8px`
                        : undefined,
                  },
                },
              })
            }
          />
        </div>
      </div>
    </div>
  );
};

const TableLayout: React.FC<{
  section: PdfLayoutSection;
  onChange: (fields: Partial<PdfLayoutSection>) => void;
}> = ({ section, onChange }) => {
  return (
    <div className='ple-section-style'>
      <span className='ple-section-style__label'>
        Questions
        <i
          className='fa fa-info-circle ple-tooltip__icon'
          data-for='question-appearance-tooltip'
          data-tip='The answers will appear the same size as the question.'
        />
        <ReactTooltip
          id='question-appearance-tooltip'
          place='top'
          effect='solid'
          className='ple-tooltip'
        />
      </span>
      <div className='ple-section-style__row'>
        <StyleFields
          label='Label'
          fields={NAME_FIELDS}
          value={section.style_config?.question_style ?? {}}
          defaultSize={DEFAULT_STYLE.question_style?.fontSize}
          onChange={(question_style) =>
            onChange({
              style_config: {
                ...(section.style_config ?? {}),
                question_style: question_style as StyleConfig['question_style'],
              },
            })
          }
        />
        <div className='ple-field'>
          <span className='ple-field__label'>
            Paddding
            <i
              className='fa fa-info-circle ple-tooltip__icon'
              data-for='question-appearance-tooltip'
              data-tip='The vertical space between two questions.'
            />
            <ReactTooltip
              id='question-appearance-tooltip'
              place='top'
              effect='solid'
              className='ple-tooltip'
            />
          </span>
          <input
            className='ple-input ple-input--size'
            type='number'
            max={100}
            step={1}
            value={parseFloat(
              String(section.style_config?.question_style?.padding),
            )}
            onChange={(e) =>
              onChange({
                style_config: {
                  ...(section.style_config ?? {}),
                  question_style: {
                    ...(section.style_config?.question_style ?? {}),
                    padding:
                      e.target.value !== ''
                        ? `${Math.min(100, Math.max(1, Math.round(Number(e.target.value)) || 1))}px 8px`
                        : undefined,
                  },
                },
              })
            }
          />
        </div>
      </div>
    </div>
  );
};

const GridLayout: React.FC<{
  columns: string[];
  section: PdfLayoutSection;
  onChange: (fields: Partial<PdfLayoutSection>) => void;
}> = ({ columns, section, onChange }) => {
  const style = section.style_config?.grid_style ?? {};
  const widthSize = style?.widthPerColumn ?? {};
  const total = columns.reduce(
    (sum, _, index) => sum + (parseFloat(String(widthSize[index] ?? '')) || 0),
    0,
  );

  return (
    <>
      <div className='ple-section-style'>
        <span className='ple-section-style__label'>Table header</span>
        <div className='ple-section-style__row'>
          <StyleFields
            label=''
            fields={NAME_FIELDS}
            value={style?.tableHeaderStyle ?? {}}
            defaultSize={DEFAULT_STYLE.grid_style?.tableHeaderStyle?.fontSize}
            onChange={(tableHeaderStyle) =>
              onChange({
                style_config: {
                  ...(section.style_config ?? {}),
                  grid_style: {
                    ...(section.style_config?.grid_style ?? {}),
                    tableHeaderStyle: tableHeaderStyle as any,
                  },
                },
              })
            }
          />
          <div className='ple-section-style__row'>
            <span className='ple-section-style__label'>Column widths</span>

            <span
              className={`ple-grid-style__total ${total === 100 ? '' : 'is-off'}`}
              title={
                total === 100
                  ? 'Columns fill the section width'
                  : 'Columns do not add up to 100% — the PDF scales them to fit'
              }
            >
              {total}% of page
            </span>

            <button
              type='button'
              className='ple-grid-style__action'
              onClick={() =>
                onChange({
                  style_config: {
                    ...(section.style_config ?? {}),
                    grid_style: {
                      ...(section.style_config?.grid_style ?? {}),
                      widthPerColumn: columns.reduce(
                        (acc, _, index) => ({
                          ...acc,
                          [index]: `${Math.floor(100 / columns.length)}`,
                        }),
                        {},
                      ),
                    },
                  },
                })
              }
            >
              Distribute evenly
            </button>
          </div>

          <div className='ple-grid-style__cols'>
            {columns.map((_, index) => (
              <div key={index} className='ple-grid-style__col'>
                <span
                  className='ple-grid-style__col-name'
                  title={columns[index]}
                >
                  {columns[index]}
                </span>

                <div className='ple-stepper'>
                  <button
                    type='button'
                    title={`Narrow ${columns[index]}`}
                    disabled={Number(widthSize[index]) <= 5}
                    onClick={() =>
                      onChange({
                        style_config: {
                          ...(section.style_config ?? {}),
                          grid_style: {
                            ...(section.style_config?.grid_style ?? {}),
                            widthPerColumn: {
                              ...(style?.widthPerColumn ?? {}),
                              [index]: `${Math.max(5, (Number(widthSize[index]) || 0) - 1)}`,
                            },
                          },
                        },
                      })
                    }
                  >
                    <i className='fa fa-minus' />
                  </button>
                  <input
                    className='ple-stepper__value'
                    type='number'
                    min={5}
                    max={100}
                    title={`${columns[index]} width (% of section)`}
                    value={widthSize[index]}
                    onChange={(e) =>
                      onChange({
                        style_config: {
                          ...(section.style_config ?? {}),
                          grid_style: {
                            ...(section.style_config?.grid_style ?? {}),
                            widthPerColumn: {
                              ...(style?.widthPerColumn ?? {}),
                              [index]: `${Math.min(100, Math.max(5, Number(e.target.value) || 5))}`,
                            },
                          },
                        },
                      })
                    }
                  />
                  <span className='ple-stepper__unit'>%</span>
                  <button
                    type='button'
                    title={`Widen ${columns[index]}`}
                    disabled={Number(widthSize[index]) >= 100}
                    onClick={() =>
                      onChange({
                        style_config: {
                          ...(section.style_config ?? {}),
                          grid_style: {
                            ...(section.style_config?.grid_style ?? {}),
                            widthPerColumn: {
                              ...(style?.widthPerColumn ?? {}),
                              [index]: `${Math.min(100, (Number(widthSize[index]) || 0) + 1)}`,
                            },
                          },
                        },
                      })
                    }
                  >
                    <i className='fa fa-plus' />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className='ple-section-style'>
        <span className='ple-section-style__label'>
          Questions
          <i
            className='fa fa-info-circle ple-tooltip__icon'
            data-for='question-appearance-tooltip'
            data-tip='The answers will appear the same size as the question.'
          />
          <ReactTooltip
            id='question-appearance-tooltip'
            place='top'
            effect='solid'
            className='ple-tooltip'
          />
        </span>
        <div className='ple-section-style__row'>
          <StyleFields
            label='Label'
            fields={NAME_FIELDS}
            value={section.style_config.question_style ?? {}}
            defaultSize={DEFAULT_STYLE.question_style?.fontSize}
            onChange={(question_style) =>
              onChange({
                style_config: {
                  ...(section.style_config ?? {}),
                  question_style:
                    question_style as StyleConfig['question_style'],
                },
              })
            }
          />
          <div className='ple-field'>
            <span className='ple-field__label'>
              Paddding
              <i
                className='fa fa-info-circle ple-tooltip__icon'
                data-for='question-appearance-tooltip'
                data-tip='The vertical space between two questions.'
              />
              <ReactTooltip
                id='question-appearance-tooltip'
                place='top'
                effect='solid'
                className='ple-tooltip'
              />
            </span>
            <input
              className='ple-input ple-input--size'
              type='number'
              max={100}
              step={1}
              value={parseFloat(
                String(section.style_config?.question_style?.padding),
              )}
              onChange={(e) =>
                onChange({
                  style_config: {
                    ...(section.style_config ?? {}),
                    question_style: {
                      ...(section.style_config?.question_style ?? {}),
                      padding:
                        e.target.value !== ''
                          ? `${Math.min(100, Math.max(1, Math.round(Number(e.target.value)) || 1))}px 8px`
                          : undefined,
                    },
                  },
                })
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};

// One text target (name / description): renders a widget per listed property.
const StyleFields: React.FC<{
  label: string;
  fields: string[];
  value: any;
  defaultSize?: string;
  onChange: (value: any) => void;
  defaultBold?: boolean;
}> = ({ label, fields, value, defaultSize, onChange, defaultBold }) => {
  return (
    <div className='ple-field'>
      <span className='ple-field__label'>{label}</span>
      <div className='ple-style-row'>
        {fields.map((field) => {
          if (field === 'fontStyle')
            return (
              <div key={field} className='ple-seg'>
                <button
                  type='button'
                  className={`ple-seg__bold ${
                    value.fontWeight
                      ? value.fontWeight === '700'
                        ? 'is-active'
                        : ''
                      : defaultBold
                        ? 'is-active'
                        : ''
                  }`}
                  onClick={() => {
                    const isBold = value.fontWeight === '700';

                    onChange({
                      ...value,
                      fontWeight: isBold ? '500' : '700',
                    });
                  }}
                >
                  B
                </button>
                <button
                  type='button'
                  className={`ple-seg__italic ${value.fontStyle === 'italic' ? 'is-active' : ''}`}
                  onClick={() =>
                    onChange({
                      ...value,
                      fontStyle:
                        value.fontStyle === 'italic' ? 'normal' : 'italic',
                    })
                  }
                >
                  I
                </button>
              </div>
            );

          if (field === 'fontSize')
            return (
              <input
                key={field}
                className='ple-input ple-input--size'
                type='number'
                min={6}
                max={72}
                placeholder='Size'
                title={`${label} font size (px)`}
                value={
                  parseInt(String(value.fontSize ?? defaultSize), 10) || ''
                }
                onChange={(e) =>
                  onChange({
                    ...value,
                    fontSize:
                      e.target.value === '' ? undefined : `${e.target.value}px`,
                  })
                }
              />
            );

          if (field === 'color' || field === 'backgroundColor')
            return (
              <input
                key={field}
                className='ple-color'
                type='color'
                title={field === 'color' ? 'Text color' : 'Background color'}
                value={(value[field] as string) ?? COLOR_DEFAULTS[field]}
                onChange={(e) =>
                  onChange({ ...value, [field]: e.target.value })
                }
              />
            );

          return null;
        })}
      </div>
    </div>
  );
};

// Per-section style controls — used by the Document Outline's inline editor.
const SectionStyleControls: React.FC<Props> = ({
  section,
  layoutType,
  columns = [],
  onChange,
}) => {
  const styleConfig = section.style_config ?? {};

  const setStyle = (fields: Partial<StyleConfig>) =>
    onChange({ style_config: { ...styleConfig, ...fields } });

  return (
    <>
      <div className='ple-section-style'>
        <span className='ple-section-style__label'>Group</span>
        <div className='ple-section-style__row'>
          <div className='ple-field'>
            <span className='ple-field__label'>Width (%)</span>
            <input
              className='ple-input ple-input--size'
              type='number'
              min={1}
              max={100}
              step={1}
              value={parseFloat(String(styleConfig.width ?? '100')) || 100}
              onChange={(e) =>
                setStyle({
                  width: `${Math.min(100, Math.max(1, Math.round(Number(e.target.value)) || 1))}%`,
                })
              }
            />
          </div>

          <StyleFields
            label='Title'
            fields={NAME_FIELDS}
            value={styleConfig.name_style ?? {}}
            defaultSize={DEFAULT_STYLE.name_style?.fontSize}
            onChange={(name_style) =>
              setStyle({ name_style: name_style as StyleConfig['name_style'] })
            }
          />
          <StyleFields
            label='Description'
            fields={DESCRIPTION_FIELDS}
            value={styleConfig.description_style ?? {}}
            defaultSize={DEFAULT_STYLE.description_style?.fontSize}
            onChange={(description_style) =>
              setStyle({
                description_style:
                  description_style as StyleConfig['description_style'],
              })
            }
          />
        </div>
      </div>

      {layoutType === 'FORM' && (
        <FormLayout
          section={section}
          onChange={(fields) => onChange({ ...section, ...fields })}
        />
      )}

      {layoutType === 'TABLE' && (
        <TableLayout
          section={section}
          onChange={(fields) => onChange({ ...section, ...fields })}
        />
      )}

      {layoutType === 'GRID' && (
        <GridLayout
          columns={columns}
          section={section}
          onChange={(fields) => onChange({ ...section, ...fields })}
        />
      )}
    </>
  );
};

export default SectionStyleControls;
